import "../../categories/mapPOIS/mapsCss.css";
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  MarkerClusterer,
  Polyline,
} from "@react-google-maps/api";
import { LoadingCircle } from "../../UI/spinners/LoadingSpinners";
import GoogleMapStyles from "../GoogleMapStyles";
import {
  AmeaCategorySingularName,
  formatMapData,
  getMarkerIcon,
  handleCenter,
} from "../helpers";
import styled from "styled-components";
import {
  myLocationMarker,
  locationOnMarker,
  defaultMarker,
  baseMarker,
  greenMark,
  purpleMark,
} from "./Icons";
import ViewModal from "../container/ViewModal";
import { getLocationData } from "../../../services/services";
import { Button } from "antd";
import { useAllContexts } from "../../../hooks/useAllContexts";
import "./MapAMEA.css";
import NewRouteMarkers from "./MapComponents/NewRouteMarkers";
import BaseCluster from "./MapComponents/BaseCluster";
import { CircleMarker } from "react-leaflet";
import { circleMarker } from "leaflet";
import BaseMarker from "./MapComponents/BaseMarker";

const customMarker = "./../../../assets/icons/Map/marker_icon_32x32.png";

const options = {
  imagePath:
    "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
};
const libraries = ["visualization", "places"];

const MapAMEA = ({
  selectedItem,
  selectedRoute,
  rightSideBox,
  data,
  selectedCategory,
  categoryId,
  showNewRoute,
  showInfrastructuresOnNewRoute,
}) => {
  const {
    newRouteData,
    ameaData,
    environment,
    hydro,
    fleet,
    bins,
    parking,
    allSensors,
  } = useAllContexts();

  const { isLoaded } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const [openInfoWindow, setOpenInfoWindow] = useState(false);

  const zoomRef = useRef(15);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(
    handleCenter(categoryId, {
      selectedCategory,
      showNewRoute,
      selectedItem: selectedItem.getItem,
    })
  );
  const [zoomLevel, setZoomLevel] = useState(12);
  const [localData, setLocalData] = useState([]);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [positionMarker, setPositionMarker] = useState("");
  const [allSensorsItem, setAllSensorsItem] = useState([]);

  useEffect(() => {
    if (!allSensors.loading) {
      if (allSensors?.allSensorsData) {
        if (categoryId === "7" || categoryId === "30")
          setAllSensorsItem(allSensors?.allSensorsData[categoryId]);
      }
    }
  }, [allSensors.loading]);

  useEffect(() => {
    selectedItem.setItem(null);
    categoryId === "4" ? setZoomLevel(1) : setZoomLevel(12);
    if (categoryId === "122" && showNewRoute) {
      setLocalData(ameaData?.get?.infrastructures.data);
    } else {
      setLocalData(
        categoryId === "12"
          ? data.filter((item) => item.details?.length > 0)
          : data
      );
    }
  }, [showNewRoute]);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!openInfoWindow) {
      setCenter(
        handleCenter(categoryId, {
          selectedCategory,
          showNewRoute,
          selectedItem: selectedItem.getItem,
        })
      );
      setZoomLevel(12);
    }
  }, [openInfoWindow]);

  const containerStyle = {
    width: "100%",
    height: "100%",
  };

  const setMarkerPosition = (position) => {
    const tempRoute = newRouteData?.get;

    tempRoute[position] = {
      address: selectedLocation?.address.house_number
        ? `${selectedLocation?.address.road} ${selectedLocation?.address.house_number}`
        : selectedLocation?.address.road,
      coordinates: {
        latitude: parseFloat(selectedLocation?.lat),
        longitude: parseFloat(selectedLocation?.lon),
      },
    };

    newRouteData?.set(position, tempRoute);
    setPositionMarker("");
    setOpenInfoWindow(false);
  };

  return (
    <MapWrapper>
      {data && isLoaded ? (
        <GoogleMap
          style={{ cursor: showNewRoute ? "pointer" : "inherit" }}
          mapContainerStyle={containerStyle}
          center={center}
          zoom={zoomLevel}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={() => setCenter(map.getCenter().toJSON())}
          onZoomChanged={() => map && (zoomRef.current = map.getZoom())}
          options={{
            styles: GoogleMapStyles,
            draggableCursor: showNewRoute ? "crosshair" : null,
          }}
          onClick={async (ev) => {
            if (categoryId === "122" && showNewRoute) {
              setSelectedCoordinates({
                lat: ev.latLng.lat(),
                lng: ev.latLng.lng(),
              });
              const location = await getLocationData(
                ev.latLng.lat(),
                ev.latLng.lng()
              ).then((response) => response);
              setSelectedLocation(location);
              setShowModal(true);
            }
          }}
        >
          {categoryId === "122" && showNewRoute && (
            <NewRouteMarkers
              positionMarker={{ get: positionMarker, set: setPositionMarker }}
              openInfoWindow={{ get: openInfoWindow, set: setOpenInfoWindow }}
            />
          )}
          {((selectedCategory !== "routes" &&
            categoryId !== "1" &&
            categoryId !== "10" &&
            categoryId !== "24" &&
            categoryId !== "7" &&
            categoryId !== "30" &&
            categoryId !== "8") ||
            (showInfrastructuresOnNewRoute &&
              selectedCategory === "routes" &&
              showNewRoute)) && (
            <BaseCluster
              data={localData}
              categoryId={categoryId}
              selectedCategory={selectedCategory}
              openInfoWindow={{
                get: openInfoWindow,
                set: setOpenInfoWindow,
              }}
              selectedItem={selectedItem}
              center={{ get: center, set: setCenter }}
              zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
              rightSideBox={rightSideBox}
              showNewRoute={showNewRoute}
            />
          )}

          {categoryId === "122" &&
            selectedCategory !== "infra" &&
            selectedRoute &&
            !showNewRoute && (
              <Polyline
                path={selectedRoute?.linestring}
                options={{
                  strokeColor: "rgb(18, 133, 181)",
                  strokeOpacity: 1,
                  strokeWeight: 10,
                }}
                geodesic={true}
              />
            )}

          {categoryId === "122" &&
            selectedCategory !== "infra" &&
            selectedRoute &&
            !showNewRoute && (
              <Marker
                noRedraw={true}
                title={selectedRoute?.start?.address}
                key={`newRoute_marker_step_start_pos`}
                position={{
                  lat: selectedRoute?.start?.latitude ?? 40.653582,
                  lng: selectedRoute?.start?.longitude ?? 22.922504,
                }}
                icon={myLocationMarker}
              ></Marker>
            )}

          {categoryId === "122" &&
            selectedCategory !== "infra" &&
            !showNewRoute &&
            selectedRoute?.infrastructures?.map((step, id) => {
              return (
                <Marker
                  noRedraw={true}
                  title={step.title}
                  key={`newRoute_marker_step_${id}`}
                  position={{
                    lat: step?.latitude ?? 40.653582,
                    lng: step?.longitude ?? 22.922504,
                  }}
                  icon={baseMarker}
                  label={{
                    className: "step-style",
                    text: `${id + 1}`,
                    color: "#fff",
                    fontSize: "16px",
                  }}
                ></Marker>
              );
            })}

          {categoryId === "122" &&
            selectedCategory !== "infra" &&
            selectedRoute &&
            !showNewRoute && (
              <Marker
                noRedraw={true}
                title={selectedRoute?.end?.address}
                key={`newRoute_marker_step_end_pos`}
                position={{
                  lat: selectedRoute?.end?.latitude ?? 40.653582,
                  lng: selectedRoute?.end?.longitude ?? 22.922504,
                }}
                icon={locationOnMarker}
              ></Marker>
            )}

          {categoryId === "1" &&
            environment?.environmentalData?.map((item, id) => {
              return (
                <BaseMarker
                  key={`environment_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
          {categoryId === "8" &&
            fleet?.fleetData?.map((item, id) => {
              return (
                <BaseMarker
                  key={`fleet_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
          {categoryId === "10" &&
            hydro?.hydroData?.map((item, id) => {
              return (
                <BaseMarker
                  key={`hydro_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
          {categoryId === "24" &&
            parking?.parkingDetailsData?.map((item, id) => {
              return (
                <BaseMarker
                  key={`parking_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
          {categoryId === "7" &&
            allSensorsItem?.map((item, id) => {
              return (
                <BaseMarker
                  key={`pois_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
          {categoryId === "30" &&
            allSensorsItem?.map((item, id) => {
              return (
                <BaseMarker
                  key={`benches_${id}`}
                  data={item}
                  categoryId={categoryId}
                  openInfoWindow={{
                    get: openInfoWindow,
                    set: setOpenInfoWindow,
                  }}
                  selectedItem={selectedItem}
                  center={{ get: center, set: setCenter }}
                  zoomLevel={{ get: zoomLevel, set: setZoomLevel }}
                  rightSideBox={rightSideBox}
                />
              );
            })}
        </GoogleMap>
      ) : (
        <LoadingIndicator>
          <LoadingText>Φόρτωση δεδομένων χάρτη...</LoadingText>
        </LoadingIndicator>
      )}

      {categoryId === "122" && (
        <ViewModal
          modalStatus={showModal}
          title="Επιλογή Σημείου"
          closeModal={() => setShowModal(false)}
        >
          <ModalContainer>
            <p
              style={{
                textAlign: "center",
                width: "400px",
                margin: "0 auto",
                fontSize: "16px",
                fontWeight: "400",
                lineHeight: "24px",
                letterSpacing: "0.5%",
              }}
            >
              Το σημείο που επιλέξατε αντιστοιχεί στην διεύθυνση:
            </p>
            <span
              style={{
                color: "rgb(0,0,180)",
                fontWeight: "600",
                fontSize: "16px",
                letterSpacing: "1px",
              }}
            >{`${selectedLocation?.address.road} ${
              selectedLocation?.address.house_number ?? ""
            }`}</span>
            <span style={{ fontSize: "12px" }}>
              Συντεταγμένες:{" "}
              {`${selectedCoordinates?.lat.toFixed(
                6
              )}, ${selectedCoordinates?.lng.toFixed(6)}`}
            </span>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                flexWrap: "wrap",
                marginTop: "20px",
              }}
            >
              <Button
                onClick={() => {
                  setMarkerPosition("start");
                }}
              >
                Προσθήκη σημείου εκίνησης
              </Button>
              <Button
                onClick={() => {
                  setMarkerPosition("end");
                }}
              >
                Προσθήκη σημείου προορισμού
              </Button>
            </div>
          </ModalContainer>
        </ViewModal>
      )}
    </MapWrapper>
  );
};

export default MapAMEA;

const MapWrapper = styled.div`
  height: calc(100vh - 100px);
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-block: 30px;
  justify-content: center;
  align-items: center;
`;

const LoadingIndicator = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
`;

const LoadingText = styled.div`
  font-size: 18px;
  letter-spacing: 1px;
`;
